// Tamil-Katral-Maiyam\src\pages\admin\comms\EmailConfigModal.tsx

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { createEmailConfig, updateEmailConfig } from '../../../modules/comms/api/comms.gateway';
import { EmailProviderConfig } from '../../../modules/comms/types';
import { showErrorToast, showSuccessToast } from '../../../utils/errorHandler';

type ProviderType = 'SMTP' | 'SENDGRID';

type EmailConfigCredentialsForm = {
  host: string;
  port: string;
  username: string;
  password: string;
  api_key: string;
};

type EmailConfigFormValues = {
  display_name: string;
  provider_type: ProviderType;
  sender_email: string;
  verification_recipient: string;
  is_active: boolean;
  is_default: boolean;
  skip_validation: boolean;
  credentials: EmailConfigCredentialsForm;
};

type EmailConfigCreatePayload = {
  tenant_id: null;
  provider_type: ProviderType;
  display_name: string;
  sender_email: string;
  verification_recipient: string;
  is_active: boolean;
  is_default: boolean;
  skip_validation: boolean;
  credentials: {
    host?: string;
    port?: string;
    username?: string;
    password?: string;
    api_key?: string;
  };
};

type EmailConfigUpdatePayload = {
  display_name: string;
  provider_type: ProviderType;
  is_active: boolean;
  verification_recipient: string;
  skip_validation: boolean;
  credentials?: EmailConfigCreatePayload['credentials'];
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PROVIDER_OPTIONS = [
  { value: 'SMTP', label: 'SMTP (e.g. SendGrid SMTP)' },
  { value: 'SENDGRID', label: 'SendGrid (REST API)' },
];

const CREATE_DEFAULTS: EmailConfigFormValues = {
  display_name: 'SendGrid SMTP (tamilschool.ai)',
  provider_type: 'SMTP',
  sender_email: 'noreply@tamilschool.ai',
  verification_recipient: '',
  is_active: true,
  is_default: true,
  skip_validation: false,
  credentials: {
    host: 'smtp.sendgrid.net',
    port: '587',
    username: 'apikey',
    password: '',
    api_key: '',
  },
};

const CHECKBOX_CLASS =
  'rounded border-app-border dark:border-gray-600 text-primary-600 focus:ring-primary-500';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: Partial<EmailProviderConfig> | null;
}

function resolveConfigId(initial?: Partial<EmailProviderConfig> | null): string | undefined {
  if (!initial) return undefined;
  const withLegacyId = initial as Partial<EmailProviderConfig> & { _id?: string };
  return withLegacyId.id ?? withLegacyId._id;
}

function mapInitialToFormValues(initial: Partial<EmailProviderConfig>): EmailConfigFormValues {
  const providerType =
    initial.provider_type === 'SENDGRID' ? 'SENDGRID' : 'SMTP';
  const creds = initial.credentials ?? {};

  return {
    display_name: initial.display_name ?? '',
    provider_type: providerType,
    sender_email: initial.sender_email ?? '',
    verification_recipient: initial.verification_recipient ?? '',
    is_active: initial.is_active ?? true,
    is_default: initial.is_default ?? false,
    skip_validation: false,
    credentials: {
      host: creds.host ?? (providerType === 'SMTP' ? 'smtp.sendgrid.net' : ''),
      port: creds.port != null ? String(creds.port) : providerType === 'SMTP' ? '587' : '',
      username: creds.username ?? (providerType === 'SMTP' ? 'apikey' : ''),
      password: '',
      api_key: creds.api_key ?? '',
    },
  };
}

function validateFormValues(values: EmailConfigFormValues, isEdit: boolean): string | null {
  if (!values.display_name.trim()) return 'Display name is required';
  if (!values.provider_type) return 'Provider type is required';

  const sender = values.sender_email.trim();
  if (!sender) return 'Sender email is required';
  if (!EMAIL_PATTERN.test(sender)) return 'Sender email must be a valid email address';

  const verification = values.verification_recipient.trim();
  if (!verification) return 'Verification recipient is required';
  if (!EMAIL_PATTERN.test(verification)) {
    return 'Verification recipient must be a valid email address';
  }

  if (values.provider_type === 'SMTP') {
    if (!values.credentials.host.trim()) return 'SMTP host is required';
    if (!String(values.credentials.port).trim()) return 'SMTP port is required';
    if (!values.credentials.username.trim()) return 'SMTP username is required';
    if (!isEdit && !values.credentials.password.trim()) {
      return 'SMTP password / API key is required';
    }
  }

  if (values.provider_type === 'SENDGRID') {
    if (!isEdit && !values.credentials.api_key.trim()) {
      return 'SendGrid API key is required';
    }
  }

  return null;
}

function buildCredentials(
  values: EmailConfigFormValues,
  isEdit: boolean
): EmailConfigCreatePayload['credentials'] | undefined {
  if (values.provider_type === 'SMTP') {
    const credentials: EmailConfigCreatePayload['credentials'] = {
      host: values.credentials.host.trim(),
      port: String(values.credentials.port).trim(),
      username: values.credentials.username.trim(),
    };
    const password = values.credentials.password.trim();
    if (password) {
      credentials.password = password;
    } else if (!isEdit) {
      credentials.password = '';
    }
    return credentials;
  }

  const apiKey = values.credentials.api_key.trim();
  if (apiKey) {
    return { api_key: apiKey };
  }
  if (!isEdit) {
    return { api_key: '' };
  }
  return undefined;
}

function buildCreatePayload(values: EmailConfigFormValues): EmailConfigCreatePayload {
  return {
    tenant_id: null,
    provider_type: values.provider_type,
    display_name: values.display_name.trim(),
    sender_email: values.sender_email.trim(),
    verification_recipient: values.verification_recipient.trim(),
    is_active: values.is_active,
    is_default: values.is_default,
    skip_validation: values.skip_validation,
    credentials: buildCredentials(values, false) ?? {},
  };
}

function buildUpdatePayload(values: EmailConfigFormValues): EmailConfigUpdatePayload {
  const payload: EmailConfigUpdatePayload = {
    display_name: values.display_name.trim(),
    provider_type: values.provider_type,
    is_active: values.is_active,
    verification_recipient: values.verification_recipient.trim(),
    skip_validation: values.skip_validation,
  };

  const credentials = buildCredentials(values, true);
  if (credentials && Object.keys(credentials).length > 0) {
    const hasSecret =
      values.provider_type === 'SMTP'
        ? Boolean(credentials.password)
        : Boolean(credentials.api_key);
    if (hasSecret) {
      payload.credentials = credentials;
    }
  }

  return payload;
}

function CheckboxField({
  id,
  label,
  helperText,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  helperText?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className={CHECKBOX_CLASS}
      />
      <div>
        <label
          htmlFor={id}
          className="text-sm font-medium text-app-text dark:text-gray-300 cursor-pointer"
        >
          {label}
        </label>
        {helperText ? (
          <p className="text-xs text-app-text-muted dark:text-gray-400 mt-0.5">{helperText}</p>
        ) : null}
      </div>
    </div>
  );
}

export const EmailConfigModal: React.FC<Props> = ({ isOpen, onClose, onSaved, initial }) => {
  const isEdit = Boolean(resolveConfigId(initial));
  const configId = resolveConfigId(initial);

  const { register, handleSubmit, reset, watch, setValue, formState, setError, clearErrors } =
    useForm<EmailConfigFormValues>({
      defaultValues: CREATE_DEFAULTS,
    });

  const providerType = watch('provider_type');
  const senderEmailValue = (watch('sender_email') || '').trim();
  const verificationRecipientValue = (watch('verification_recipient') || '').trim();
  const isActive = watch('is_active');
  const isDefault = watch('is_default');
  const skipValidation = watch('skip_validation');

  useEffect(() => {
    if (!isOpen) return;
    clearErrors();
    if (initial) {
      reset(mapInitialToFormValues(initial));
    } else {
      reset(CREATE_DEFAULTS);
    }
  }, [isOpen, initial, reset, clearErrors]);

  const onSubmit = async (values: EmailConfigFormValues) => {
    const validationMessage = validateFormValues(values, isEdit);
    if (validationMessage) {
      setError('root', { type: 'manual', message: validationMessage });
      showErrorToast(validationMessage);
      return;
    }

    try {
      if (isEdit && configId) {
        await updateEmailConfig(configId, buildUpdatePayload(values));
        showSuccessToast('Email config updated');
      } else {
        await createEmailConfig(buildCreatePayload(values));
        showSuccessToast('Email config created');
      }
      onSaved();
      onClose();
    } catch (err) {
      showErrorToast(err);
    }
  };

  const rootError = formState.errors.root?.message;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Email Config' : 'Create Email Config'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Display name"
          placeholder="SendGrid SMTP (tamilschool.ai)"
          {...register('display_name', { required: 'Display name required' })}
          error={
            formState.errors.display_name
              ? String(formState.errors.display_name.message)
              : undefined
          }
        />

        <Select
          label="Provider type"
          options={PROVIDER_OPTIONS}
          {...register('provider_type', { required: true })}
          error={
            formState.errors.provider_type
              ? String(formState.errors.provider_type.message)
              : undefined
          }
        />

        <Input
          label="Sender email"
          type="email"
          placeholder={senderEmailValue || 'noreply@tamilschool.ai'}
          readOnly={isEdit}
          helperText={
            isEdit
              ? 'Sender email cannot be changed after creation.'
              : undefined
          }
          {...register('sender_email', { required: 'Sender email required' })}
          error={
            formState.errors.sender_email
              ? String(formState.errors.sender_email.message)
              : undefined
          }
        />

        <Input
          label="Verification recipient"
          type="email"
          placeholder={verificationRecipientValue || 'you@example.com'}
          {...register('verification_recipient', {
            required: 'Verification recipient is required',
          })}
          error={
            formState.errors.verification_recipient
              ? String(formState.errors.verification_recipient.message)
              : undefined
          }
        />

        {providerType === 'SMTP' ? (
          <div className="space-y-4 rounded-lg border border-app-border dark:border-gray-700 p-4">
            <p className="text-sm font-medium text-app-text dark:text-gray-200">SMTP credentials</p>
            <Input
              label="SMTP host"
              placeholder="smtp.sendgrid.net"
              {...register('credentials.host', { required: 'SMTP host is required' })}
              error={
                formState.errors.credentials?.host
                  ? String(formState.errors.credentials.host.message)
                  : undefined
              }
            />
            <Input
              label="SMTP port"
              placeholder="587"
              {...register('credentials.port', { required: 'SMTP port is required' })}
              error={
                formState.errors.credentials?.port
                  ? String(formState.errors.credentials.port.message)
                  : undefined
              }
            />
            <Input
              label="SMTP username"
              placeholder="apikey"
              {...register('credentials.username', { required: 'SMTP username is required' })}
              error={
                formState.errors.credentials?.username
                  ? String(formState.errors.credentials.username.message)
                  : undefined
              }
            />
            <Input
              label="SMTP password / API key"
              type="password"
              autoComplete="new-password"
              placeholder={isEdit ? 'Leave blank to keep existing credential' : 'SG....'}
              helperText={
                isEdit
                  ? 'Leave password/API key blank to keep existing credential.'
                  : 'Use your SendGrid API key as the SMTP password.'
              }
              {...register('credentials.password', {
                required: isEdit ? false : 'SMTP password / API key is required',
              })}
              error={
                formState.errors.credentials?.password
                  ? String(formState.errors.credentials.password.message)
                  : undefined
              }
            />
          </div>
        ) : (
          <div className="space-y-4 rounded-lg border border-app-border dark:border-gray-700 p-4">
            <p className="text-sm font-medium text-app-text dark:text-gray-200">SendGrid credentials</p>
            <Input
              label="SendGrid API key"
              type="password"
              autoComplete="new-password"
              placeholder={isEdit ? 'Leave blank to keep existing credential' : 'SG....'}
              helperText={
                isEdit
                  ? 'Leave password/API key blank to keep existing credential.'
                  : 'Mail Send–only keys may fail validation; use SMTP provider or skip validation.'
              }
              {...register('credentials.api_key', {
                required: isEdit ? false : 'SendGrid API key is required',
              })}
              error={
                formState.errors.credentials?.api_key
                  ? String(formState.errors.credentials.api_key.message)
                  : undefined
              }
            />
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-app-border dark:border-gray-700 p-4">
          <CheckboxField
            id="is_active"
            label="Active"
            checked={Boolean(isActive)}
            onChange={(checked) => setValue('is_active', checked, { shouldDirty: true })}
          />
          <CheckboxField
            id="is_default"
            label="Default provider for tenant"
            checked={Boolean(isDefault)}
            disabled={isEdit}
            helperText={
              isEdit
                ? 'Default flag can only be set when creating a config.'
                : 'Use for the global platform email provider.'
            }
            onChange={(checked) => setValue('is_default', checked, { shouldDirty: true })}
          />
          <CheckboxField
            id="skip_validation"
            label="Skip validation"
            checked={Boolean(skipValidation)}
            helperText="Save without verifying credentials with the provider (status stays UNVALIDATED)."
            onChange={(checked) => setValue('skip_validation', checked, { shouldDirty: true })}
          />
        </div>

        {rootError ? (
          <p className="text-sm text-error-600 dark:text-error-400">{rootError}</p>
        ) : null}

        <div className="flex gap-2 justify-end pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={formState.isSubmitting}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};
